import { Component, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { BehaviorSubject, merge, Observable, of } from 'rxjs';
import { catchError, map, share, switchMap, tap } from 'rxjs/operators';
import { FetchArgs, PagingToken } from 'src/app/shared/models/core';
import { CandidateGridItem } from 'src/app/recruiting/candidates/grid/models/candidate-grid-item';
import { CandidateGridResponse } from 'src/app/recruiting/candidates/grid/models/candidate-grid-response';
import { RecruitingService } from 'src/app/recruiting/recruiting.service';
import { IdentityService } from 'src/app/core/identity.service';
import { groupBy, GroupResult, orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { FollowupTime } from 'src/app/shared/models/followup-time.model';
import { CandidateAssignment } from 'src/app/shared/models/enums/candidate-assignment.enum';
import { UnsubscribeOnDestroy } from 'src/app/shared/utilities/virtual-images/unsubscribe-ondestroy';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

const DEFAULT_PAGE_SIZE = 50;

@UntilDestroy()
@Component({
    selector: 'nova-candidate-search-grid',
    templateUrl: './candidate-search-grid.component.html',
    styleUrls: ['./candidate-search-grid.component.scss']
})
export class CandidateSearchGridComponent extends UnsubscribeOnDestroy {
    data$: Observable<PagingToken<GroupResult[] | CandidateGridItem[]>>;
    fetchArgs$: BehaviorSubject<FetchArgs>;
    isLoading$ = new BehaviorSubject<boolean>(false);
    count = 0;
    recruiterId = null;
    timeEntries: FollowupTime[] = [];
    filter = null;
    candidateAssignment = CandidateAssignment;

    get defaultFetchArgs(): FetchArgs {
        return {
            filter: {},
            sort: { field: 'firstName', dir: 'asc' },
            skip: 0,
            pageSize: DEFAULT_PAGE_SIZE
        };
    }

    constructor(
        @Inject(DOCUMENT) private readonly document: Document,
        private readonly recruitingService: RecruitingService,
        private readonly identityService: IdentityService
    ) {
        super();
        this.recruiterId = identityService.current.contactId;
    }

    quickSearchCandidates(filter) {
        this.filter = {
            ...filter,
            recruiterId: this.recruiterId
        };

        const fetchArgs = {
            ...this.defaultFetchArgs,
            filter: this.filter
        };

        if (!this.fetchArgs$) {
            this.fetchArgs$ = new BehaviorSubject<FetchArgs>(fetchArgs);
            this.getData();
        } else {
            this.fetchArgs$.next(fetchArgs);
        }
    }

    load(args: FetchArgs) {
        this.fetchArgs$.next({
            ...args,
            filter: this.filter
        });
    }

    openEmailClient(email: string) {
        this.document.location.href = 'mailto:' + email;
    }

    getAssignmentLabel(assignment: CandidateAssignment): string {
        switch (assignment) {
            case CandidateAssignment.Your:
                return 'Your Candidates';
            case CandidateAssignment.Others:
                return 'Other Candidates - Assigned';
            case CandidateAssignment.Unassigned:
                return 'Other Candidates - Unassigned';
        }
    }

    private getData() {
        this.data$ = merge(
            of({
                data: [],
                total: 0,
                totalWithoutFilter: 0
            }),
            this.fetchArgs$.pipe(
                untilDestroyed(this),
                tap((_) => this.isLoading$.next(true)),
                switchMap((args: FetchArgs) => {
                    return this.recruitingService.quickSearchCandidates(args);
                }),
                map((result) => {
                    const descriptor: SortDescriptor[] = [{ field: 'assignment', dir: 'asc' }];
                    return {
                        data: groupBy(orderBy(result.data, descriptor), descriptor),
                        total: result.total
                    };
                }),
                tap((resp) => {
                    this.count = resp?.total ?? 0;
                    this.isLoading$.next(false);
                }),
                catchError((_) => {
                    this.isLoading$.next(false);
                    return of(<CandidateGridResponse>{});
                }),
                share() //prevent re-fetching if more than one subscriber
            )
        );
    }
}
